import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { closeLoaderWithMessage, openLoaderWithMessage, showMessage } from "../messages/messagesActions";
import { IUserTransaction } from "./manageMembership.types";

// ==================== MEMBERSHIP DETAILS ACTIONS ====================

// Fetch Membership Details Actions
export const FETCH_MEMBERSHIP_DETAILS_PROGRESS = "FETCH_MEMBERSHIP_DETAILS_PROGRESS";
export const FETCH_MEMBERSHIP_DETAILS_SUCCESS = "FETCH_MEMBERSHIP_DETAILS_SUCCESS";
export const FETCH_MEMBERSHIP_DETAILS_FAILURE = "FETCH_MEMBERSHIP_DETAILS_FAILURE";

export const fetchMembershipDetailsProgress = () => action(FETCH_MEMBERSHIP_DETAILS_PROGRESS);
export const fetchMembershipDetailsSuccess = (data: IUserTransaction[]) =>
  action(FETCH_MEMBERSHIP_DETAILS_SUCCESS, data);
export const fetchMembershipDetailsFailure = (errorMessage: string) =>
  action(FETCH_MEMBERSHIP_DETAILS_FAILURE, errorMessage);

export const getMembershipDetailsByCodeAsync =
  (
    searchFilter: string,
    searchValue: string,
    onCallBack?: (isSuccess?: boolean, data?: IUserTransaction[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchMembershipDetailsProgress());
        const res = await api.get(
          `/get-details-by-membershipcode?${searchFilter}=${searchValue.trim()}`
        );
        const data: IUserTransaction[] = res?.data || [];
        dispatch(fetchMembershipDetailsSuccess(data));
        if (onCallBack) onCallBack(true, data);
      } catch (err: any) {
        const errorMsg = err?.response?.data?.message || "Failed to fetch membership details";
        dispatch(fetchMembershipDetailsFailure(errorMsg));
        if (onCallBack) onCallBack(false);
        dispatch(
          showMessage({
            message: errorMsg,
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

export const getMembershipDetailsAsync =
  (
    membershipCode: string,
    onCallBack?: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.get(
          `/get-membership-details?membership_code=${membershipCode}`
        );
        const data = res?.data || [];
        if (onCallBack) onCallBack(true, data);
      } catch (err: any) {
        if (onCallBack) onCallBack(false);
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch membership details",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// ==================== INVOICE & BRANCH OFFICE ACTIONS ====================

export const getInvoiceDataAsync =
  (
    fromDate: string,
    toDate: string,
    status: string,
    onCallBack?: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<Promise<any>, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.get(
          `/get-invoice-data?from_date=${fromDate}&to_date=${toDate}&status=${status}`
        );
        const data = res?.data?.result || [];
        if (onCallBack) onCallBack(true, data);
        return data;
      } catch (err: any) {
        if (onCallBack) onCallBack(false);
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch invoice data",
            type: "error",
            displayAs: "snackbar",
          })
        );
        return [];
      }
    };

export const listBranchOfficesAsync =
  (
    onCallBack?: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.get(`/list-branch-offices`);
        const data = res?.data || [];
        if (onCallBack) onCallBack(true, data);
      } catch (err: any) {
        if (onCallBack) onCallBack(false);
        dispatch(
          showMessage({
            message: err?.response?.data?.msg || err?.response?.data?.message || "Failed to fetch branch offices",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// ==================== FAMILY PACKAGE & VEHICLE ACTIONS ====================

export const addVehicleInFamilyPackageAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/add-vehicle-in-family-package-it", values);
        dispatch(
          showMessage({
            message: "Vehicle Added Successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const errorMsg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to add vehicle.";
        dispatch(
          showMessage({
            message: errorMsg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const getFamilyAddedVehiclesAsync =
  (
    membershipCode: string,
    onCallBack?: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.get(
          `/get-family-added-vehicles?membership_code_v=${membershipCode}`
        );
        const data = res?.data?.result || [];
        if (onCallBack) onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch vehicles.",
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

// ==================== USER PROSPECT & REFERRAL ACTIONS ====================

export const getUserProspectIdAsync =
  (
    membershipCode: string,
    onCallBack?: (isSuccess?: boolean, prospectId?: number) => void
  ): ThunkAction<Promise<number | null>, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.get("/get-user-prospectid", {
          params: { membership_code: membershipCode },
        });
        if (res.data?.length > 0) {
          const maxId = res.data.reduce(
            (maxId: number, prospect: { user_prospect_id: number }) => {
              return prospect.user_prospect_id > maxId
                ? prospect.user_prospect_id
                : maxId;
            },
            -Infinity
          );
          if (onCallBack) onCallBack(true, maxId);
          return maxId;
        } else {
          throw new Error("No user_prospect_id found in the response.");
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Error fetching user_prospect_id.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
        return null;
      }
    };

export const assignReferralCodeAsync =
  (
    payload: { user_prospect_id_v: number; referral_code_v: string },
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/bo-assign-referral-code", payload);
        dispatch(
          showMessage({
            message: "Referral is assigned successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.result || err?.response?.data?.message || "Failed to assign referral.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const getReferralsAsync =
  (
    searchValue?: string,
    onCallBack?: (isSuccess?: boolean, data?: any[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        let apiEndpoint = "";
        if (searchValue) {
          apiEndpoint = `?name=${searchValue}`;
        }
        const res = await api.get(`/referrals${apiEndpoint}`);
        const data: any[] = res.data.result || [];
        if (onCallBack) onCallBack(true, data);
      } catch (err: any) {
        if (onCallBack) onCallBack(false);
      }
    };

// ==================== USER UPDATE ACTIONS ====================

export const updateUserAddressAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-user-address", values);
        dispatch(
          showMessage({
            message: "Address Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update address.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateAmountByITAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-amount-by-IT", values);
        dispatch(
          showMessage({
            message: "Amount Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update amount.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateUserEmailAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-user-email", values);
        dispatch(
          showMessage({
            message: "Email Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update email.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateUserNameAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-user-name", values);
        dispatch(
          showMessage({
            message: "Name Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update name.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateUserContactNumberAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-user-contact-number", values);
        dispatch(
          showMessage({
            message: "Number Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update contact number.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateRsaPackageAsync =
  (
    payload: { membership_code: string; packageId: any },
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-rsa-package", payload);
        dispatch(
          showMessage({
            message: "Package Updated Successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update package.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateSaleDateAsync =
  (
    values: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await api.post("/update-sale-date", values);
        dispatch(
          showMessage({
            message: "Sale Date Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update sale date.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };

export const updateVehicleDetailsAsync =
  (
    isNewMembership: boolean,
    payload: any,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const url = isNewMembership
          ? "/bo-update-vehicle-number"
          : "/update-gold-vehicle-details";
        await api.post(url, payload);
        dispatch(
          showMessage({
            message: "Vehicle Details Updated successfully.",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(true);
      } catch (err: any) {
        const msg = err?.response?.data?.msg || err?.response?.data?.message || "Failed to update vehicle details.";
        dispatch(
          showMessage({
            message: msg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) onCallBack(false);
      }
    };
